import {useElements} from '@stripe/react-stripe-js';

import {
  useUpdateCustomerDefaultPaymentMethodMutation,
  useUpgradeCustomerToProPlanMutation,
  type WeaveIngestionLimitInMb,
} from '../../../generated/graphql';
import {captureError} from '../../../integrations';
import {Analytics} from '../../../services/analytics';
import {
  type CheckoutPlan,
  confirmPayment,
  type Org,
} from '../../../util/accounts/pricing';
import {
  doNotRetryContext,
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../../util/errors';
import {type SubmitParams} from './NewStripeForm';

type ErrOpts = {
  err: unknown;
  op: string;
  userFacingErrorMsg: string;
  analyticsEvent?: string;
  extra?: {[key: string]: any};
};
export function handleUpgradeSubError(
  opts: ErrOpts,
  org: Org,
  plan: CheckoutPlan,
  seats: number
): string | null {
  const {err, op, userFacingErrorMsg, analyticsEvent} = opts;
  if (!(err instanceof Error) && typeof err !== 'string') {
    return null;
  }
  const extra = {
    org: org.id,
    plan: plan.id,
    units: seats,
    location: 'new upgrade subscription form',
    ...opts.extra,
  };
  captureError(err, `NewUpgradeSubscriptionForm-${op}`, {
    extra,
  });
  if (analyticsEvent != null) {
    Analytics.track(analyticsEvent, extra);
  }
  return userFacingErrorMsg;
}

export const useUpgradeSubscriptionToProPlan = () => {
  const elements = useElements();

  const [upgradeCustomerToProPlanMutation] =
    useUpgradeCustomerToProPlanMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  const [updateDefaultPaymentMethodMutation] =
    useUpdateCustomerDefaultPaymentMethodMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  return async (
    {
      stripe,
      paymentMethodID,
      setPaymentMethodAsDefault,
      setErrMsg,
    }: SubmitParams,
    plan: CheckoutPlan,
    seats: number,
    org: Org,
    isWeaveOnlyUser: boolean | null,
    weaveIngestionTier: WeaveIngestionLimitInMb,
    isMonthly: boolean
  ): Promise<boolean> => {
    if (!isWeaveOnlyUser && seats < org.usedSeats) {
      setErrMsg(
        `You are using ${org.usedSeats} Full Model Seats on your organization, so you need at least ${org.usedSeats} seats.`
      );
      return false;
    }
    if (elements == null) {
      return false;
    }

    const subResult = await upgrade();
    if (subResult == null) {
      return false;
    }

    const {organizationID, subscription} = subResult;

    // if the purchase was successful and the set payment method flag is true,
    // set the payment method as default
    if (setPaymentMethodAsDefault && paymentMethodID != null) {
      const {errors} = await updateDefaultPaymentMethodMutation({
        variables: {
          organizationId: organizationID,
          paymentMethod: paymentMethodID,
        },
      });

      // since the subscription itself was successful, just surface the error
      if (errors != null) {
        const errMsg = extractErrorMessageFromApolloError(errors);
        if (errMsg != null) {
          setErrMsg(errMsg);
        }
      }
    }

    const paymentIntent = subscription.latest_invoice.payment_intent;
    const clientSecret = paymentIntent?.client_secret;
    const paymentIntentStatus = paymentIntent?.status;
    const needsPayment =
      ['requires_action', 'requires_payment_method'].indexOf(
        paymentIntentStatus
      ) !== -1;

    if (needsPayment) {
      const paymentConfirmed = await confirmPayment(
        clientSecret,
        stripe,
        elements,
        'primary',
        setErrMsg
      );
      if (!paymentConfirmed) {
        return false;
      }
    }

    Analytics.track('Upgrade Subscription to Pro Plan Completed', {
      org: org.id,
      plan: plan.id,
      units: seats,
      location: 'new upgrade subscription form',
    });
    return true;

    // HELPER FUNCTIONS
    async function upgrade(): Promise<{
      organizationID: string;
      subscription: {
        latest_invoice: any;
      };
    } | null> {
      if (paymentMethodID == null) {
        throw new Error(`missing paymentMethodID`);
      }

      try {
        const upgradeSubResult = await upgradeCustomerToProPlanMutation({
          variables: {
            organizationID: org.id,
            planID: plan.id,
            paymentMethod: paymentMethodID,
            modelsSeats: seats,
            weaveIngestionTier: weaveIngestionTier,
            monthly: isMonthly,
          },
        });

        const customerInfo = upgradeSubResult.data?.upgradeCustomerToProPlan;
        const sub = customerInfo?.subscriptionCommit;
        const orgID = customerInfo?.organizationID;

        if (upgradeSubResult.errors != null || sub == null || orgID == null) {
          const errMsg = handleUpgradeSubError(
            {
              err: "Unknown error upgrading subscription. Exception didn't throw but success was not reported.",
              op: 'handleUpgrade-unknown',
              analyticsEvent: 'Upgrade Subscription Error from checkout modal',
              extra: {errorType: 'unknown'},
              userFacingErrorMsg:
                'Error confirming payment. Please wait a few moments or contact support@wandb.com for help.',
            },
            org,
            plan,
            seats
          );
          if (errMsg != null) {
            setErrMsg(errMsg);
          }
          return null;
        }

        return {organizationID: orgID, subscription: sub};
      } catch (err) {
        const errMsg = extractErrorMessageFromApolloError(err);
        const errString = handleUpgradeSubError(
          {
            err,
            op: 'upgradeSubResult',
            extra: {
              reason: errMsg,
              errorType: 'processing payment',
            },
            analyticsEvent: 'Upgrade Subscription Error',
            userFacingErrorMsg: `Error processing payment: ${errMsg}`,
          },
          org,
          plan,
          seats
        );
        if (errString != null) {
          setErrMsg(errString);
        }
        return null;
      }
    }
  };
};
